import {
  Button,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableRow,
} from "@material-ui/core";
import { ExportIcon } from "assets/icons/CustomSVGIcons";
import { ITableColumn } from "components/interfaces";
import { IMeasurementListRow } from "components/interfaces/IMeasurementListRow";
import { ExportStore, MeasurementListStore, MessageStore } from "store";
import { formatNumber, formatNumberToMonetary } from "utils";
import { lang } from "../../../i18nLang";
import { HeadTable } from "../../UI/Table";
import MeasurementRow from "./MeasurementRow";

const columns: ITableColumn[] = [
  { dataKey: "description", label: lang("fields.description"), width: 140 },
  {
    dataKey: "total",
    label: lang("fields.measured"),
    width: 140,
    monetary: true,
  },
  {
    dataKey: "allDiscountsTotal",
    label: lang("fields.discountsTotal"),
    width: 140,
    monetary: true,
  },
  {
    dataKey: "grossTotal",
    label: lang("measurements.table.grossInvoiceAmount"),
    width: 140,
    monetary: true,
  },
  {
    dataKey: "retentionAdvance",
    label: lang("fields.receivableTotal"),
    width: 142,
    monetary: true,
  },
  {
    dataKey: "taxesTotal",
    label: lang("fields.taxesTotal"),
    width: 140,
    monetary: true,
  },
  {
    dataKey: "receivableTotal",
    label: lang("fields.billing"),
    width: 140,
    monetary: true,
  },
  { dataKey: "status", label: lang("fields.status"), width: 140 },
];

const useStyles = makeStyles({
  cellFooter: {
    fontSize: "0.875rem",
    fontWeight: 700,
    color: "rgba(0, 0, 0, 0.87)",
  },
});

interface IProps {
  rows: IMeasurementListRow[];
  onEdit: Function;
  onSendToBilling: (uid: string) => void;
  onShowBilling: (uid: string) => void;
  onAttachFiles: (uid: string) => void;
  onChangeStatus: (uid: string, newStatus: string) => void;
  onChangeDates: (uid: string, initialDate: Date, finalDate: Date) => void;
  onExpandDetails: (uid: string) => void;
  style?: any;
}

const MeasurementsTable = ({
  rows,
  onEdit,
  onSendToBilling,
  onShowBilling,
  onAttachFiles,
  onChangeStatus,
  onChangeDates,
  onExpandDetails,
  style = {},
}: IProps) => {
  const classes = useStyles();
  const totals = MeasurementListStore.getTotals(rows);

  const handleClickExport = () => {
    MessageStore.setLoading(true);
    const rowDatas = rows.map((row) => ({
      ...row,
      status: lang(`measurements.status.${row.status}`),
    }));
    const footerData = {
      description: lang("exportData.measurements.totals"),
      total: totals.measurements,
      allDiscountsTotal: totals.discounts,
      grossTotal: totals.grossTotal,
      retentionAdvance: totals.retentionAndAdvances,
      taxesTotal: totals.taxes,
      receivableTotal: totals.netTotal,
      measuredPercentual: totals.measuredPercentTotal,
      status: "",
      rowspan: 2,
    };

    ExportStore.exportMeasurementsList(rowDatas, footerData).finally(() =>
      MessageStore.setLoading(false)
    );
  };

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      style={{ overflow: "scroll", height: "calc(99vh - 131px)", ...style }}
    >
      <Table aria-label="collapsible table">
        <HeadTable
          className="measurements-table-header"
          columns={columns}
          actionColumn={true}
          style={{
            fontSize: "0.688rem",
          }}
        />
        <TableBody>
          {rows.map((row, index) => {
            return (
              <MeasurementRow
                key={index}
                row={row}
                columns={columns}
                onEdit={onEdit}
                onSendToBilling={onSendToBilling}
                onShowBilling={onShowBilling}
                onAttachFiles={onAttachFiles}
                onChangeStatus={onChangeStatus}
                onChangePeriod={onChangeDates}
                onExpandDetails={onExpandDetails}
              />
            );
          })}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableCell className={classes.cellFooter}>
              {lang("fields.total")}
            </TableCell>
            {Object.keys(totals).map((key) => (
              <TableCell className={classes.cellFooter}>
                {key === "measuredPercentTotal"
                  ? `${formatNumber(totals[key], 2)}%`
                  : formatNumberToMonetary(totals[key])}
              </TableCell>
            ))}
            <TableCell colSpan={2} align="right">
              <Button
                color="primary"
                startIcon={<ExportIcon fill={"#757575"} />}
                onClick={handleClickExport}
              >
                {lang("export")}
              </Button>
            </TableCell>
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
};

export default MeasurementsTable;
